import { useState } from 'react';

import { useTranslation } from '@tankef/i18n';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '@tankef/icons';
import { cn } from '@tankef/ui';

/**
 * An action attached to an announcement slide. Three forms:
 *
 *   navigate — router link (internal or external href)
 *   mailto   — opens the default mail client with preset recipient + subject
 *   callback — custom side-effect (modal open, API call, etc.)
 *
 * Backoffice will send `navigate` or `mailto` (serializable). `callback`
 * is FE-only for programmatic actions.
 */
export type AnnouncementAction =
  | { type: 'navigate'; href: string; label: string }
  | { type: 'mailto'; email: string; subject?: string; label: string }
  | { type: 'callback'; label: string; onPress: () => void };

/**
 * A single announcement slide.
 *
 * Priority bands (lower = shown first):
 *   0–99   CRITICAL  — system / BE emergency (always floats above everything)
 *   100–199 ONBOARDING — FE-driven (KYC=100, CLABE=110, Signature=120, Pagacel=130)
 *   200–299 BACKOFFICE_HIGH — backoffice informational, below onboarding
 *   300+   BACKOFFICE_LOW  — promotional / low urgency
 *
 * Backoffice can pin an announcement *above* onboarding by using priority < 100,
 * or *below* by using priority > 199.
 */
export interface Announcement {
  id: string;
  /** Visual tone of the slide. */
  variant: 'info' | 'warning' | 'error' | 'success';
  /** Main text shown in the bar. Keep under ~80 chars for mobile. */
  message: string;
  /** Controls sort order across all announcement sources. */
  priority: number;
  /** Who produced this announcement — useful for filtering and debugging. */
  source: 'onboarding' | 'backoffice' | 'system';
  /** When false the × button is hidden. Defaults to true. */
  dismissible?: boolean;
  action?: AnnouncementAction;
}

interface TopAnnouncementBarProps {
  announcements: Announcement[];
  onDismiss?: (id: string) => void;
}

// Text colors chosen for WCAG AA/AAA compliance against each background:
//   info    #29D6B2 teal   + black → 11.7:1 AAA
//   warning #f59e0b amber  + black → 10.1:1 AAA
//   success #22c55e green  + black →  7.5:1 AAA
//   error   #EF5350 red    + white →  3.3:1 (only AA-large); darkened to error-700 + white → ~5:1
const toneStyles: Record<Announcement['variant'], string> = {
  info: 'bg-primary-500 text-black',
  warning: 'bg-warning-500 text-black',
  error: 'bg-error-700 text-white',
  success: 'bg-success-500 text-black',
};

// Action button border/hover adapts to text color (black vs white contexts)
const actionButtonClassDark =
  'whitespace-nowrap rounded-full border border-black/30 px-3 py-0.5 text-label font-semibold uppercase tracking-wider hover:bg-black/10 transition-colors';
const actionButtonClassLight =
  'whitespace-nowrap rounded-full border border-white/70 px-3 py-0.5 text-label font-semibold uppercase tracking-wider hover:bg-white/15 transition-colors';

const darkTextVariants: Announcement['variant'][] = ['info', 'warning', 'success'];

function ActionButton({ action, darkText }: { action: AnnouncementAction; darkText: boolean }) {
  const cls = darkText ? actionButtonClassDark : actionButtonClassLight;

  if (action.type === 'navigate') {
    return (
      <a href={action.href} className={cls}>
        {action.label}
      </a>
    );
  }

  if (action.type === 'mailto') {
    const href = `mailto:${action.email}${action.subject ? `?subject=${encodeURIComponent(action.subject)}` : ''}`;
    return (
      <a href={href} className={cls}>
        {action.label}
      </a>
    );
  }

  return (
    <button type="button" onClick={action.onPress} className={cls}>
      {action.label}
    </button>
  );
}

export function TopAnnouncementBar({ announcements, onDismiss }: TopAnnouncementBarProps) {
  const { t } = useTranslation('dashboard');
  const [index, setIndex] = useState(0);

  if (!announcements.length) return null;

  const safeIndex = Math.min(index, announcements.length - 1);
  const current = announcements[safeIndex]!;
  const total = announcements.length;
  const hasMany = total > 1;
  const isDismissible = current.dismissible !== false;
  const darkText = darkTextVariants.includes(current.variant);
  const navBtnClass = cn(
    'rounded-full p-0.5 transition-colors',
    darkText
      ? 'opacity-70 hover:bg-black/10 hover:opacity-100'
      : 'opacity-80 hover:bg-white/15 hover:opacity-100'
  );

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-3 px-4 py-2 text-xs font-medium',
        toneStyles[current.variant]
      )}
      role="status"
      aria-live="polite"
      aria-label={`Anuncio ${safeIndex + 1} de ${total}`}
    >
      {hasMany && (
        <button
          type="button"
          onClick={() => setIndex(i => (i - 1 + total) % total)}
          className={navBtnClass}
          aria-label="Anuncio anterior"
        >
          <ChevronLeftIcon className="h-3.5 w-3.5" />
        </button>
      )}

      <span className="truncate">{current.message}</span>

      {current.action && <ActionButton action={current.action} darkText={darkText} />}

      {hasMany && (
        <>
          <span className={cn('tabular-nums', darkText ? 'opacity-70' : 'opacity-80')}>
            {safeIndex + 1} / {total}
          </span>
          <button
            type="button"
            onClick={() => setIndex(i => (i + 1) % total)}
            className={navBtnClass}
            aria-label="Siguiente anuncio"
          >
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </button>
        </>
      )}

      {onDismiss && isDismissible && (
        <button
          type="button"
          onClick={() => onDismiss(current.id)}
          className={cn(
            'rounded-full p-1 transition-colors',
            darkText
              ? 'opacity-70 hover:bg-black/10 hover:opacity-100'
              : 'opacity-80 hover:bg-white/15 hover:opacity-100'
          )}
          aria-label={t('alerts.dismiss')}
        >
          <CloseIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
